import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Search, Loader2, CheckCircle, AlertCircle, Gift, Package as PackageIcon } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Add Complimentary eSIM" modal — Customers tab.
 *
 * Admin searches for a destination → picks a package → confirms.
 * Uses the existing /api/esim/generate flow with a synthetic
 * `admin-comp-{uuid}` session_id which bypasses payment verification
 * server-side. The resulting order is flagged is_complimentary: true.
 */
export const AddComplimentaryEsimModal = ({ customer, onClose, onCreated }) => {
  const { allCountries, fetchPlansForCountry } = useApp();
  const [countryQuery, setCountryQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState(null);

  const filteredCountries = useMemo(() => {
    if (!countryQuery.trim()) return allCountries.slice(0, 20);
    const q = countryQuery.toLowerCase();
    return allCountries
      .filter((c) => c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q))
      .slice(0, 20);
  }, [allCountries, countryQuery]);

  useEffect(() => {
    if (!selectedCountry) return;
    let cancelled = false;
    (async () => {
      setLoadingPlans(true);
      setPlans([]);
      setSelectedPlan(null);
      try {
        const fetched = await fetchPlansForCountry(selectedCountry.id);
        if (!cancelled) setPlans(fetched || []);
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCountry, fetchPlansForCountry]);

  const handleConfirm = async () => {
    if (!customer?.email || !selectedCountry || !selectedPlan) return;
    setSubmitting(true);
    setFlash(null);
    try {
      const sessionId = `admin-comp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const payload = {
        session_id: sessionId,
        package_id: selectedPlan.packageId || selectedPlan.id,
        user_email: customer.email,
        country_id: selectedCountry.id,
        country_name: selectedCountry.name,
        plan_name: selectedPlan.name,
        data_amount: selectedPlan.dataAmount,
        duration_days: selectedPlan.durationDays,
        price: 0,
        is_unlimited: selectedPlan.isUnlimited || false,
      };
      const resp = await axios.post(`${API}/esim/generate`, payload, { timeout: 60000 });
      if (resp.data?.success) {
        setFlash({ type: 'ok', msg: `Complimentary eSIM created for ${customer.email}` });
        if (onCreated) onCreated(resp.data);
        setTimeout(onClose, 900);
      } else {
        setFlash({ type: 'err', msg: resp.data?.detail || 'Provisioning failed' });
      }
    } catch (e) {
      setFlash({ type: 'err', msg: e.response?.data?.detail || e.message || 'Provisioning failed' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="add-comp-esim-modal"
    >
      <div className="bg-white dark:bg-neutral-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-100 dark:border-neutral-800 shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-neutral-800">
          <div className="flex items-center gap-2">
            <Gift size={20} className="text-emerald-600" />
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100">
                Add Complimentary eSIM
              </h3>
              <p className="text-xs text-gray-500 dark:text-neutral-500">
                For {customer?.email}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-800"
            data-testid="close-add-comp-esim-btn"
          >
            <X size={20} className="text-gray-600 dark:text-neutral-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Step 1: pick a country */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-neutral-400 mb-2 uppercase tracking-wide">
              1. Destination
            </label>
            {!selectedCountry ? (
              <>
                <div className="relative mb-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={countryQuery}
                    onChange={(e) => setCountryQuery(e.target.value)}
                    placeholder="Search country or code…"
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-sm focus:outline-none focus:border-black dark:focus:border-white"
                    data-testid="comp-esim-country-search"
                    autoFocus
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                  {filteredCountries.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCountry(c)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800 text-left"
                      data-testid={`comp-esim-country-${c.id}`}
                    >
                      <img src={c.flag} alt="" className="w-6 h-4 rounded object-cover" />
                      <span className="text-sm truncate text-gray-800 dark:text-neutral-200">{c.name}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-emerald-200 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10">
                <div className="flex items-center gap-2">
                  <img src={selectedCountry.flag} alt="" className="w-7 h-5 rounded object-cover" />
                  <span className="font-semibold text-gray-900 dark:text-neutral-100">
                    {selectedCountry.name}
                  </span>
                  <span className="text-xs font-mono text-gray-500">{selectedCountry.id}</span>
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedCountry(null); setSelectedPlan(null); setPlans([]); }}
                  className="text-xs text-emerald-700 dark:text-emerald-300 font-semibold hover:underline"
                >
                  Change
                </button>
              </div>
            )}
          </div>

          {/* Step 2: pick a plan */}
          {selectedCountry && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-neutral-400 mb-2 uppercase tracking-wide">
                2. Package
              </label>
              {loadingPlans ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-neutral-500">
                  <Loader2 size={14} className="animate-spin" />
                  Loading packages…
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {plans.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-neutral-500">No packages available.</p>
                  )}
                  {plans.map((p) => {
                    const isSel = selectedPlan?.id === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelectedPlan(p)}
                        className={
                          `w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left ` +
                          (isSel
                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10'
                            : 'border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800')
                        }
                        data-testid={`comp-esim-plan-${p.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <PackageIcon size={16} className="text-gray-500" />
                          <div>
                            <p className="font-semibold text-sm text-gray-900 dark:text-neutral-100">
                              {p.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-neutral-500">
                              {p.dataAmount} GB · {p.durationDays} days
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 line-through">
                          A${(p.price || 0).toFixed(2)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {flash && (
            <div
              className={
                `flex items-center gap-2 text-sm rounded-lg px-3 py-2 ` +
                (flash.type === 'ok'
                  ? 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-500/10'
                  : 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-500/10')
              }
            >
              {flash.type === 'ok' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              <span>{flash.msg}</span>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100 dark:border-neutral-800 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 dark:border-neutral-700 text-sm font-semibold text-gray-700 dark:text-neutral-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedPlan || submitting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-black text-white text-sm font-bold hover:bg-gray-800 disabled:opacity-40 dark:border dark:border-white"
            data-testid="confirm-add-comp-esim-btn"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />}
            Create complimentary eSIM
          </button>
        </div>
      </div>
    </div>
  );
};


/**
 * "Complimentary Top-Up" modal — Order View.
 *
 * Reuses the /api/esim/topup/packages/{iccid} endpoint to fetch compatible
 * top-up packages for the target order's eSIM, then hits /api/esim/topup
 * with price=0 and a synthetic admin-comp session_id so the topup_history
 * entry is flagged is_complimentary.
 */
export const ComplimentaryTopupModal = ({ order, onClose, onCompleted }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    if (!order?.iccid) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await axios.get(`${API}/esim/topup/packages/${order.iccid}`);
        const list =
          (Array.isArray(resp.data?.packages) && resp.data.packages) ||
          (Array.isArray(resp.data?.data?.packages) && resp.data.data.packages) ||
          (Array.isArray(resp.data?.data) && resp.data.data) ||
          [];
        const formatted = list.map((p, idx) => {
          const mb = p.data?.dataAmount;
          const gb = mb != null ? mb / 1024 : parseFloat(p.dataAmount || p.data_amount || 0) || 0;
          return {
            id: p.packageId || p.id || `pkg_${idx}`,
            packageId: p.packageId || p.package_id || p.id,
            name: p.package || p.name || 'Top-Up Package',
            dataAmount: gb,
            days: p.day || p.duration || p.duration_days || 0,
            price: parseFloat(p.price || p.retailPrice || 0) || 0,
          };
        }).filter((p) => p.packageId);
        if (!cancelled) setPackages(formatted);
      } catch (e) {
        if (!cancelled) setFlash({ type: 'err', msg: e.response?.data?.detail || 'Could not load top-up packages' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [order?.iccid]);

  const handleConfirm = async () => {
    if (!selected || !order?.iccid) return;
    setSubmitting(true);
    setFlash(null);
    try {
      const sessionId = `admin-comp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const resp = await axios.post(`${API}/esim/topup`, {
        iccid: order.iccid,
        package_id: selected.packageId,
        session_id: sessionId,
        data_amount: selected.dataAmount,
        duration_days: selected.days,
        plan_name: selected.name,
        price: 0,
      }, { timeout: 60000 });
      if (resp.data?.success) {
        setFlash({ type: 'ok', msg: 'Complimentary top-up applied.' });
        if (onCompleted) onCompleted(resp.data);
        setTimeout(onClose, 900);
      } else {
        setFlash({ type: 'err', msg: resp.data?.detail || resp.data?.message || 'Top-up failed' });
      }
    } catch (e) {
      setFlash({ type: 'err', msg: e.response?.data?.detail || e.message || 'Top-up failed' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="comp-topup-modal"
    >
      <div className="bg-white dark:bg-neutral-900 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col border border-gray-100 dark:border-neutral-800 shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-neutral-800">
          <div className="flex items-center gap-2">
            <Gift size={20} className="text-emerald-600" />
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100">
                Complimentary Top-Up
              </h3>
              <p className="text-xs text-gray-500 dark:text-neutral-500">
                {order?.country_name} · ICCID {order?.iccid?.slice(-6)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-800"
            data-testid="close-comp-topup-btn"
          >
            <X size={20} className="text-gray-600 dark:text-neutral-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-neutral-500">
              <Loader2 size={14} className="animate-spin" />
              Loading top-up packages…
            </div>
          ) : packages.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-neutral-500">No top-up packages available for this eSIM.</p>
          ) : (
            packages.map((p) => {
              const isSel = selected?.id === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelected(p)}
                  className={
                    `w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left ` +
                    (isSel
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10'
                      : 'border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800')
                  }
                  data-testid={`comp-topup-package-${p.id}`}
                >
                  <div>
                    <p className="font-semibold text-sm text-gray-900 dark:text-neutral-100">{p.name}</p>
                    <p className="text-xs text-gray-500 dark:text-neutral-500">
                      {p.dataAmount} GB · {p.days} days
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 line-through">
                    A${(p.price || 0).toFixed(2)}
                  </span>
                </button>
              );
            })
          )}

          {flash && (
            <div
              className={
                `flex items-center gap-2 text-sm rounded-lg px-3 py-2 ` +
                (flash.type === 'ok'
                  ? 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-500/10'
                  : 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-500/10')
              }
            >
              {flash.type === 'ok' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              <span>{flash.msg}</span>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100 dark:border-neutral-800 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 dark:border-neutral-700 text-sm font-semibold text-gray-700 dark:text-neutral-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selected || submitting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-black text-white text-sm font-bold hover:bg-gray-800 disabled:opacity-40 dark:border dark:border-white"
            data-testid="confirm-comp-topup-btn"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />}
            Apply top-up
          </button>
        </div>
      </div>
    </div>
  );
};
